import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Container from '../../components/shared/Container';
import Button from '../../components/shared/Button';
import { Link } from 'react-router-dom';
import { useRegion } from '../../context/RegionContext';
import { useHeader } from '../../context/HeaderContext';
import SEO from '../../components/shared/SEO';
import StructuredData from '../../components/shared/StructuredData';
import { generateBreadcrumbSchema } from '../../utils/schema';
import { LOYALTY_PROGRAM_UNDER_MAINTENANCE } from '../../config/featureFlags';

// Animation variants
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.8 } }
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1,
    transition: {
      staggerChildren: 0.3
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut"
    }
  }
};

export default function LoyaltyPage() {
  return LOYALTY_PROGRAM_UNDER_MAINTENANCE
    ? <LoyaltyProgramComingSoon />
    : <LoyaltyProgramContent />;
}

function LoyaltyProgramComingSoon() {
  const { setUseBlurredBackground } = useHeader();

  useEffect(() => {
    setUseBlurredBackground(true);

    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO
        title="Loyalty Rewards Program | The Original Pancake House"
        description="We're improving the Original Pancake House Loyalty Program. Check back soon for details on new rewards, perks, and how to join."
        canonicalUrl="/loyalty"
        openGraph={{
          image: "/oph-social-share4.jpg"
        }}
      />

      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Loyalty Program', url: '/loyalty' }
      ])} />

      {/* Hero Section */}
      <motion.div
        className="relative h-[400px] bg-brand-navy"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `url(/images/loyalty-original-pancake-house.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Gradient Overlay - Darker on mobile for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>

        <Container className="relative h-full">
          <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end">
              {/* Left side - Title and breadcrumb */}
              <div className="mb-4 md:mb-0">
                <div className="mb-4">
                  <div className="flex items-center text-sm font-medium">
                    <Link
                      to="/"
                      className="text-white hover:text-brand-cream transition-colors"
                    >
                      Home
                    </Link>

                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>

                    <span className="text-white text-opacity-90">
                      Loyalty Program
                    </span>
                  </div>
                </div>
                <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">We're Improving Our Loyalty Program</h1>
              </div>

              {/* Right side - Description */}
              <div className="md:max-w-xl">
                <p className="text-white text-opacity-90 drop-shadow-md">
                  Our rewards program is getting an upgrade. Sign-ups are paused for now while we build something better for our guests.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </motion.div>

      {/* Coming Soon Message */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            animate="visible"
            className="max-w-3xl mx-auto text-center"
          >
            <motion.div variants={itemVariants} className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-8">
              <img src="/icons/pancake.svg" alt="" aria-hidden="true" className="w-10 h-10" />
            </motion.div>

            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy mb-6">
              CHECK BACK SOON
            </motion.h2>

            <motion.p variants={itemVariants} className="text-lg text-gray-700 mb-4">
              We're hard at work making the OPH Rewards Program even better, with new ways to earn, better rewards, and a simpler sign-up experience.
            </motion.p>

            <motion.p variants={itemVariants} className="text-lg text-gray-700 mb-10">
              New enrollments are temporarily unavailable. Existing points and rewards are safe, and we'll share full details here as soon as the new program launches.
            </motion.p>

            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                as={Link}
                to="/locations"
                variant="solid"
                color="navy"
              >
                Find a Location
              </Button>
              <Button
                as={Link}
                to="/contact"
                variant="outline"
                color="navy"
              >
                Contact Us
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}

function LoyaltyProgramContent() {
  const { selectedRegion } = useRegion();
  const { setUseBlurredBackground } = useHeader();
  const [region, setRegion] = useState(selectedRegion || 'washington');

  // Enable blurred background for header on loyalty page
  useEffect(() => {
    setUseBlurredBackground(true);
    
    // Clean up when component unmounts
    return () => {
      setUseBlurredBackground(false);
    };
  }, [setUseBlurredBackground]);

  useEffect(() => {
    if (selectedRegion) {
      setRegion(selectedRegion);
    }
  }, [selectedRegion]);

  // Define the URLs for different regions
  const loyaltyUrls = {
    washington: 'https://deals.spoton.com/6a581d76a6bb7843788b5627/join-loyalty',
    'new-york': 'https://deals.spoton.com/6a581d76a6bb7843788b5627/join-loyalty',
    indiana: 'https://deals.spoton.com/6707ded522600f0ac5a6bfe3/join-loyalty'
  };

  return (
    <div className="bg-brand-cream min-h-screen">
      <SEO 
        title="Loyalty Rewards Program | The Original Pancake House" 
        description="Join The Original Pancake House Loyalty Program and earn 1 point for every dollar spent. Redeem 75 points for $5 off, 150 points for $10 off, or 250 points for $20 off, plus more delicious perks along the way. Sign up free today."
        canonicalUrl="/loyalty"
        openGraph={{
          image: "/oph-social-share4.jpg"
        }}
      />
      
      {/* Structured Data for SEO */}
      <StructuredData data={generateBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'Loyalty Program', url: '/loyalty' }
      ])} />
      
      {/* Hero Section */}
      <motion.div 
        className="relative h-[400px] bg-brand-navy"
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        style={{
          backgroundImage: `url(/images/loyalty-original-pancake-house.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        {/* Gradient Overlay - Darker on mobile for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-navy via-brand-navy/60 to-brand-navy/30 lg:via-transparent lg:to-transparent"></div>
        
        <Container className="relative h-full">
          <div className="absolute bottom-8 left-0 right-0 w-full px-6 md:px-8 lg:px-12">
            <div className="flex flex-col md:flex-row md:justify-between md:items-end">
              {/* Left side - Title and breadcrumb */}
              <div className="mb-4 md:mb-0">
                <div className="mb-4">
                  <div className="flex items-center text-sm font-medium">
                    <Link 
                      to="/"
                      className="text-white hover:text-brand-cream transition-colors"
                    >
                      Home
                    </Link>
                    
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mx-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    
                    <span className="text-white text-opacity-90">
                      Loyalty Program
                    </span>
                  </div>
                </div>
                <h1 className="text-5xl md:text-6xl font-heading text-white drop-shadow-lg">OPH Rewards Program</h1>
              </div>
              
              {/* Right side - Description */}
              <div className="md:max-w-xl">
                <p className="text-white text-opacity-90 drop-shadow-md">
                  Your OPH loyalty program just got a tasty upgrade. Earn 1 point for every dollar spent, receive points automatically every time you check out, and redeem rewards anytime at participating Original Pancake House locations.
                </p>
              </div>
            </div>
          </div>
        </Container>
      </motion.div>

      {/* How It Works Section */}
      <section className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              HERE'S HOW IT WORKS
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {/* Step 1 */}
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-6">
                  <img src="/icons/mobile.svg" alt="Mobile phone" className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">SIGN UP WITH YOUR PHONE NUMBER</h3>
                <p className="text-gray-700">
                  Join in seconds, then give the same phone number each time you visit
                </p>
              </motion.div>

              {/* Step 2 */}
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-6">
                  <img src="/icons/dollar-sign.svg" alt="Dollar sign" className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">EARN 1 POINT FOR EVERY $1</h3>
                <p className="text-gray-700">
                  Points are added automatically every time you check out — no card or app required
                </p>
              </motion.div>

              {/* Step 3 */}
              <motion.div variants={itemVariants} className="text-center">
                <div className="w-24 h-24 rounded-full border-2 border-brand-navy flex items-center justify-center bg-white mx-auto mb-6">
                  <img src="/icons/pancake.svg" alt="Pancake stack" className="w-10 h-10" />
                </div>
                <h3 className="text-xl font-bold text-brand-navy mb-2">REDEEM REWARDS ANYTIME</h3>
                <p className="text-gray-700">
                  Turn your points into $5, $10, or $20 off your meal whenever you're ready
                </p>
              </motion.div>
            </div>

            {/* Existing points reassurance */}
            <motion.div variants={itemVariants} className="bg-brand-navy text-white p-8 rounded-lg text-center mb-16">
              <h3 className="text-3xl font-heading mb-2">GOOD NEWS!</h3>
              <p className="text-xl mb-6">ALL PREVIOUSLY EARNED POINTS REMAIN ACTIVE AND READY TO USE.</p>
              <Button 
                as="a"
                href="#sign-up"
                variant="solid" 
                color="cream"
                className="mx-auto"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('sign-up').scrollIntoView({ behavior: 'smooth' });
                }}
              >
                SIGN UP NOW
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* Rewards */}
      <section className="py-16 bg-brand-cream">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-5xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-4">
              WATCH THE REWARDS ADD UP
            </motion.h2>

            <motion.p variants={itemVariants} className="text-lg text-gray-700 text-center max-w-2xl mx-auto mb-12">
              Earn 1 point for every dollar spent, then redeem your points whenever you like.
            </motion.p>

            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {/* $5 Reward */}
              <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-brand-navy text-white p-6 text-center">
                  <h3 className="text-3xl font-heading">75 POINTS</h3>
                </div>
                <div className="p-6 text-center">
                  <p className="text-4xl font-heading text-brand-navy mb-3">$5 OFF</p>
                  <p className="text-gray-700">Your first reward is closer than you think</p>
                </div>
              </motion.div>

              {/* $10 Reward */}
              <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-brand-navy text-white p-6 text-center">
                  <h3 className="text-3xl font-heading">150 POINTS</h3>
                </div>
                <div className="p-6 text-center">
                  <p className="text-4xl font-heading text-brand-navy mb-3">$10 OFF</p>
                  <p className="text-gray-700">Keep visiting and your savings grow</p>
                </div>
              </motion.div>

              {/* $20 Reward */}
              <motion.div variants={itemVariants} className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="bg-brand-navy text-white p-6 text-center">
                  <h3 className="text-3xl font-heading">250 POINTS</h3>
                </div>
                <div className="p-6 text-center">
                  <p className="text-4xl font-heading text-brand-navy mb-3">$20 OFF</p>
                  <p className="text-gray-700">Our biggest reward, on us</p>
                </div>
              </motion.div>
            </div>

            <motion.p variants={itemVariants} className="text-xl font-bold text-brand-navy text-center">
              And more delicious perks along the way!
            </motion.p>
          </motion.div>
        </Container>
      </section>

      {/* Sign Up Section */}
      <section id="sign-up" className="py-16 bg-white">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-4">
              JOIN OUR LOYALTY PROGRAM
            </motion.h2>

            <motion.p variants={itemVariants} className="text-lg text-gray-700 text-center max-w-2xl mx-auto mb-12">
              Not a member yet? Join now and start earning today. Choose the program for the locations you visit most.
            </motion.p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Washington/New York Card */}
              <motion.div 
                variants={itemVariants}
                className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-transparent"
              >
                <div className="bg-brand-navy text-white p-6">
                  <h3 className="text-2xl font-heading mb-2">WASHINGTON & NEW YORK</h3>
                  <p>Sign up for our rewards program if you visit our locations in Washington or New York</p>
                </div>
                <div className="p-6 flex flex-col items-center">
                  <img 
                    src="/images/loyalty/loyalty.jpg" 
                    alt="Washington and New York Loyalty Program" 
                    className="w-full h-48 object-cover rounded-md mb-6"
                    loading="lazy"
                  />
                  <Button 
                    as="a"
                    href={loyaltyUrls['washington']}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="solid" 
                    color="navy"
                    className="w-full"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    }
                  >
                    Sign Up Now
                  </Button>
                </div>
              </motion.div>

              {/* Indiana Card */}
              <motion.div 
                variants={itemVariants}
                className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-transparent"
              >
                <div className="bg-brand-navy text-white p-6">
                  <h3 className="text-2xl font-heading mb-2">INDIANA</h3>
                  <p>Sign up for our rewards program if you visit our locations in Indiana</p>
                </div>
                <div className="p-6 flex flex-col items-center">
                  <img 
                    src="/images/loyalty/loyalty-2.jpg" 
                    alt="Indiana Loyalty Program" 
                    className="w-full h-48 object-cover rounded-md mb-6"
                    loading="lazy"
                  />
                  <Button 
                    as="a"
                    href={loyaltyUrls['indiana']}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="solid" 
                    color="navy"
                    className="w-full"
                    icon={
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    }
                  >
                    Sign Up Now
                  </Button>
                </div>
              </motion.div>
            </div>

            {/* Already a member section */}
            <motion.div variants={itemVariants} className="mt-16 text-center">
              <h3 className="text-2xl font-heading text-brand-navy mb-4">ALREADY A MEMBER?</h3>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                There's nothing to do. Your rewards follow your phone number, so just give it to your server when you check out. All previously earned points remain active and ready to use.
              </p>
            </motion.div>
          </motion.div>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-brand-cream">
        <Container>
          <motion.div
            variants={sectionVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <motion.h2 variants={itemVariants} className="text-4xl font-heading text-brand-navy text-center mb-12">
              FREQUENTLY ASKED QUESTIONS
            </motion.h2>

            <div className="space-y-6">
              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-brand-navy mb-2">How do I earn points?</h3>
                <p className="text-gray-700">
                  You earn 1 point for every $1 spent at participating Original Pancake House locations. Just give your phone number when you check out and your points are added automatically.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-brand-navy mb-2">How do I redeem my rewards?</h3>
                <p className="text-gray-700">
                  Redeem 75 points for $5 off, 150 points for $10 off, or 250 points for $20 off. You can redeem anytime — just let your server know you'd like to use your rewards when you place your order.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-brand-navy mb-2">What happened to the points I already earned?</h3>
                <p className="text-gray-700">
                  Good news — all previously earned points remain active and ready to use. There's nothing you need to do to transfer them.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-brand-navy mb-2">Do I need a card or an app?</h3>
                <p className="text-gray-700">
                  No. Your rewards are tied to your phone number, so there's nothing to carry, download, or remember.
                </p>
              </motion.div>

              <motion.div variants={itemVariants} className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-xl font-bold text-brand-navy mb-2">Can I use my rewards at any location?</h3>
                <p className="text-gray-700">
                  You can earn and redeem rewards at any participating Original Pancake House location. Note that Washington/New York and Indiana run separate programs, so sign up for the one that matches the locations you visit.
                </p>
              </motion.div>
            </div>

            <motion.div variants={itemVariants} className="mt-12 text-center">
              <p className="text-gray-700 mb-4">
                Have more questions about our loyalty program?
              </p>
              <Button 
                href="/contact"
                variant="outline" 
                color="navy"
              >
                Contact Us
              </Button>
            </motion.div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
}
